import React, {useEffect, useState} from 'react';
import {Grid, Spin, Tag, Typography} from 'antd';
import NewsService from '../../service/newsService';
import {FilterOption, FiltersResponse} from '../../types/cardData';
import {useTheme} from '../ThemeProvider';

const {Text} = Typography;
const {useBreakpoint} = Grid;

// Module-level cache so re-mounts across page navigations don't re-fetch
let cachedFilters: FiltersResponse | null = null;

const STORAGE_KEY_REGION = 'newsRegionFilter';
const STORAGE_KEY_RELEVANCE = 'newsRelevanceFilter';

export interface NewsFilterValues {
    region: string | undefined;
    relevance: string;
}

function loadSaved(): NewsFilterValues {
    try {
        return {
            region: localStorage.getItem(STORAGE_KEY_REGION) || undefined,
            relevance: localStorage.getItem(STORAGE_KEY_RELEVANCE) || 'all',
        };
    } catch {
        return { region: undefined, relevance: 'all' };
    }
}

export function useNewsFilters() {
    const [filters, setFilters] = React.useState<NewsFilterValues>(loadSaved);

    const updateFilters = React.useCallback((next: NewsFilterValues) => {
        setFilters(next);
        try {
            if (next.region) {
                localStorage.setItem(STORAGE_KEY_REGION, next.region);
            } else {
                localStorage.removeItem(STORAGE_KEY_REGION);
            }
            if (next.relevance && next.relevance !== 'all') {
                localStorage.setItem(STORAGE_KEY_RELEVANCE, next.relevance);
            } else {
                localStorage.removeItem(STORAGE_KEY_RELEVANCE);
            }
        } catch { /* ignore */ }
    }, []);

    return { filters, setFilters: updateFilters };
}

interface NewsFilterBarProps {
    filters: NewsFilterValues;
    onChange: (filters: NewsFilterValues) => void;
}

const NewsFilterBar: React.FC<NewsFilterBarProps> = ({ filters, onChange }) => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { isDark } = useTheme();

    const [regionOptions, setRegionOptions] = useState<FilterOption[]>(cachedFilters?.regions ?? []);
    const [relevanceOptions, setRelevanceOptions] = useState<FilterOption[]>(cachedFilters?.relevance ?? []);
    const [loading, setLoading] = useState(!cachedFilters);

    useEffect(() => {
        if (cachedFilters) return;
        let cancelled = false;
        NewsService.getFiltersV2().then((data) => {
            if (cancelled) return;
            cachedFilters = data;
            setRegionOptions(data.regions);
            setRelevanceOptions(data.relevance);
            setLoading(false);
        }).catch(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, []);

    const chipStyle = (active: boolean): React.CSSProperties => ({
        cursor: 'pointer',
        margin: 0,
        fontSize: isMobile ? '12px' : '13px',
        padding: isMobile ? '2px 8px' : '4px 12px',
        borderRadius: '16px',
        fontWeight: active ? 600 : 400,
        border: active ? undefined : `1px solid ${isDark ? '#434343' : '#d9d9d9'}`,
        userSelect: 'none',
    });

    if (loading) {
        return <div style={{textAlign: 'center', padding: '8px'}}><Spin size="small" /></div>;
    }

    if (regionOptions.length === 0 && relevanceOptions.length === 0) return null;

    return (
        <div style={{
            padding: isMobile ? '0 16px' : '0 24px',
            maxWidth: screens.md ? '1000px' : '100%',
            margin: '0 auto 16px',
            position: 'sticky',
            top: '64px',
            zIndex: 99,
            background: isDark ? '#141414' : '#f0f2f5',
            paddingTop: '8px',
            paddingBottom: '4px',
        }}>
            <div style={{
                border: `1px solid ${isDark ? '#303030' : '#e8e8e8'}`,
                borderRadius: '12px',
                padding: isMobile ? '12px 16px' : '16px 24px',
                background: isDark ? '#1a1a1a' : '#fafafa',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
            }}>
                {relevanceOptions.length > 0 && (
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                        <Text type="secondary" style={{fontSize: '13px', minWidth: '50px'}}>Stories</Text>
                        {relevanceOptions.map((opt) => (
                            <Tag
                                key={opt.value}
                                color={filters.relevance === opt.value ? 'red' : undefined}
                                style={chipStyle(filters.relevance === opt.value)}
                                onClick={() => onChange({...filters, relevance: opt.value})}
                            >
                                {opt.label}
                            </Tag>
                        ))}
                    </div>
                )}
                {regionOptions.length > 0 && (
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                        <Text type="secondary" style={{fontSize: '13px', minWidth: '50px'}}>Region</Text>
                        <Tag
                            color={!filters.region ? 'red' : undefined}
                            style={chipStyle(!filters.region)}
                            onClick={() => onChange({...filters, region: undefined})}
                        >
                            All
                        </Tag>
                        {regionOptions.map((r) => (
                            <Tag
                                key={r.value}
                                color={filters.region === r.value ? 'red' : undefined}
                                style={chipStyle(filters.region === r.value)}
                                onClick={() => onChange({...filters, region: r.value})}
                            >
                                {r.label}
                            </Tag>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NewsFilterBar;
